import { inject, Injectable, signal } from '@angular/core';
import { PageHeader, PageSection, TableColumn } from '../lib/interfaces/common';
import { Title } from '@angular/platform-browser';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DateTime } from 'luxon';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  titleService = inject(Title);
  private pageHeaderSignal = signal<PageHeader>({} as PageHeader);
  private fb = inject(FormBuilder);

  constructor() {}

  set pageHeader(value: Partial<PageHeader>) {
    this.pageHeaderSignal.update((prev) => ({ ...prev, ...value }));

    if (value.pageTitle) this.titleService.setTitle(value.pageTitle);
  }

  get pageHeader() {
    return this.pageHeaderSignal();
  }

  mapFields<T>(items: T[], fields: TableColumn<T>[]): T[] {
    const dateColumns = fields.filter(
      (col) => col.columnType === 'date' || col.columnType === 'timestamp',
    );
    let data = items.map((d) => {
      dateColumns.forEach((col) => {
        if (d[col.field]) {
          d[col.field] =
            col.columnType === 'date'
              ? (this.dateConverter(d[col.field] as string) as any)
              : new Date(d[col.field] as string);
        }
      });
      return d;
    });
    return data;
  }
  createEmptyRowGroup<T>(
    columns: TableColumn<T>[],
    dataKey: string,
  ): FormGroup {
    const group: any = {};
    group[dataKey] = [null];
    group['hasSaved'] = [false];
    columns
      .filter((col) => col.formField)
      .forEach((col) => {
        group[col.formField?.fieldName] = [null, col.formField?.validators];
        if (col.formField?.dependentFields?.length) {
          col.formField?.dependentFields.forEach(
            (fieldName) => (group[fieldName] = [null]),
          );
        }
      });

    return this.fb.group(group);
  }

  isPartialMatch(
    partial: Record<string, any>,
    full: Record<string, any>,
  ): boolean {
    return Object.keys(partial).every((key) => {
      if (key === 'hasSaved') {
        return true;
      }

      return (
        full.hasOwnProperty(key) &&
        full[key] === partial[key] &&
        key !== 'hasSaved'
      );
    });
  }

  createPageForm<T>(sections: PageSection<T>[], dataKey: string): FormGroup {
    const group: any = {};
    group[dataKey] = [null];
    const fields = sections.flatMap((section) => section.sectionFields);

    fields
      .filter((field) => field.formField)
      .forEach((field) => {
        group[field.formField?.fieldName] = [null, field.formField?.validators];
        if (field.formField?.dependentFields?.length) {
          field.formField?.dependentFields.forEach(
            (fieldName) => (group[fieldName] = [null]),
          );
        }
      });

    return this.fb.group(group);
  }

  dateConverter(dateString: string) {
    let date;
    try {
      const dt = DateTime.fromSQL(dateString);
      date = dt.toJSDate();
    } catch (err) {
      console.log('Date Error going to fallback');
      date = new Date(dateString);
    }

    return date;
  }

  dateConverttoEST(date: Date) {
    if (!date) return;
    return DateTime.fromJSDate(date).toSQLDate();
  }

  dateConverttoESTDate(date: Date, type: 'date' | 'timestamp') {
    if (!date) return;
    let _date = DateTime.fromJSDate(date);
    _date.setZone('America/New_York');
    if (type === 'timestamp') {
      return _date.toFormat('MM/dd/yyyy HH:mm:ss');
    } else {
      return _date.toFormat('MM/dd/yyyy');
    }
  }

  getStateStorage(storageType: 'session' | 'local') {
    if (storageType === 'local') {
      return localStorage;
    } else {
      return sessionStorage;
    }
  }
}
